import classNames from 'classnames'
import { useCallback, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'

export interface CollapsibleSectionProps {
  title: string
  storageKey: string
  defaultExpanded?: boolean
  children: React.ReactNode
  icon?: React.ReactNode
  accentColor?: 'primary' | 'secondary' | 'slate' | 'yellow'
}

export function CollapsibleSection({
  title,
  storageKey,
  defaultExpanded = true,
  children,
  icon,
  accentColor = 'primary',
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === 'undefined') return defaultExpanded
    const stored = localStorage.getItem(storageKey)
    return stored !== null ? stored === 'true' : defaultExpanded
  })

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => {
      const newValue = !prev
      localStorage.setItem(storageKey, String(newValue))
      return newValue
    })
  }, [storageKey])

  const accentStyles = {
    primary: 'border-l-primary-400 dark:border-l-primary-500 bg-primary-50/50 dark:bg-primary-500/5',
    secondary: 'border-l-secondary-400 dark:border-l-secondary-500 bg-secondary-50/50 dark:bg-secondary-500/5',
    slate: 'border-l-slate-300 dark:border-l-slate-600 bg-slate-50/50 dark:bg-slate-500/5',
    yellow: 'border-l-yellow-400 dark:border-l-yellow-500 bg-yellow-50/50 dark:bg-yellow-500/5',
  }

  return (
    <div className="mt-5">
      <button
        onClick={toggleExpanded}
        className={classNames(
          'flex items-center justify-between w-full px-3 py-2.5 mb-2 text-sm font-semibold transition-all duration-200 rounded-xl border-l-4',
          accentStyles[accentColor],
          'text-slate-700 dark:text-slate-200 hover:brightness-95 dark:hover:brightness-110'
        )}
      >
        <span className="flex items-center gap-2.5">
          {icon}
          {title}
        </span>
        <FaChevronDown
          className={classNames(
            'text-xs transition-transform duration-300 text-slate-400 dark:text-slate-500',
            { 'rotate-180': !isExpanded }
          )}
        />
      </button>
      <div
        className={classNames(
          'space-y-1 overflow-hidden transition-all duration-300 ease-out',
          {
            'max-h-0 opacity-0': !isExpanded,
            'max-h-[2000px] opacity-100': isExpanded,
          }
        )}
      >
        {children}
      </div>
    </div>
  )
}

export default CollapsibleSection

